import React, { useState, useEffect } from 'react'
import { Container, Table, Header, Segment, Loader, Button, Message, Icon, Form, TextArea, Menu, Input, Pagination, Confirm } from 'semantic-ui-react'
import axios from 'axios'
import { format } from 'date-fns'

const Emails = () => {
    const [emails, setEmails] = useState([])
    const [filteredEmails, setFilteredEmails] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const [activePage, setActivePage] = useState(0)
    const [itemsPerPage] = useState(10)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [selectedEmail, setSelectedEmail] = useState(null)
    const [sortDirection, setSortDirection] = useState('descending')
    const [sortColumn, setSortColumn] = useState('date')
    const [replyContent, setReplyContent] = useState('')
    const [replyError, setReplyError] = useState(null)
    const [replySuccess, setReplySuccess] = useState(null)
    const [activeTab, setActiveTab] = useState('inbox')
    const [isReplying, setIsReplying] = useState(false)
    const [showDeleteConfirmationPopup, setShowDeleteConfirmationPopup] = useState(false)
    const [idToDelete, setIdToDelete] = useState(null)
    const [deleteConfirmContentString, setDeleteConfirmContentString] = useState('')

    const fetchEmails = async () => {
        try {
            setLoading(true)
            const endpoint = activeTab === 'inbox' ? '/api/emails' : '/api/sent-emails'
            const res = await axios.get(endpoint)
            setEmails(res.data)
            setFilteredEmails(res.data)
        } catch (err) {
            setError('Failed to load emails. Please try again later.')
            console.error('Error fetching emails:', err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchEmails()
    }, [activeTab])

    useEffect(() => {
        setSelectedEmail(null)
        setReplyError(null)
        setReplySuccess(null)
        setSearchQuery('')
        setActivePage(0)
        setIsReplying(false)
    }, [activeTab])

    useEffect(() => {
        const filtered = emails.filter(email =>
            (email.to?.toLowerCase().includes(searchQuery.toLowerCase()) ||
             email.from?.toLowerCase().includes(searchQuery.toLowerCase()) ||
             email.subject?.toLowerCase().includes(searchQuery.toLowerCase()))
        )
        setFilteredEmails(filtered)
        setActivePage(0)
    }, [searchQuery, emails])

    const handleSearchChange = e => {
        setSearchQuery(e.target.value)
    }

    const handlePageChange = (e, { activePage }) => {
        setActivePage(activePage - 1)
    }

    const handleEmailClick = email => {
        setSelectedEmail(email)
        setReplyContent('')
        setReplyError(null)
        setReplySuccess(null)
    }

    const handleSort = clickedColumn => {
        if (sortColumn !== clickedColumn) {
            setSortColumn(clickedColumn)
            setSortDirection('ascending')
            setEmails([...emails].sort((a, b) => {
                    if (clickedColumn === 'to') return a.to.localeCompare(b.to)
                    if (clickedColumn === 'from') return a.from.localeCompare(b.from)
                    if (clickedColumn === 'subject') return a.subject.localeCompare(b.subject)
                    if (clickedColumn === 'date') return new Date(a.date) - new Date(b.date)
                    return 0
            }))
        } else {
            setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending')
            setEmails([...emails].reverse())
        }
    }
    const handleSendReply = async () => {
        if (!replyContent.trim()) {
            setReplyError('Reply content cannot be empty')
            return
        }
        try {
            setReplyError(null)
            setReplySuccess(null)
            await axios.post('/api/send-reply', {
                originalEmailId: selectedEmail.id,
                replyBody: replyContent,
                fromAddress: selectedEmail.to,
                toAddress: selectedEmail.from,
                originalMessageId: selectedEmail.messageId,
                originalSubject: selectedEmail.subject
            })
            setReplySuccess('Reply sent successfully!')
            setReplyContent('')
        } catch (err) {
            setReplyError('Failed to send reply: ' + (err.response?.data?.error || err.message))
        }
    }
    const handleDeleteClicked = (e, email) => {
        e.stopPropagation()
        if (showDeleteConfirmationPopup) return
        const contentString = "Delete email '" + email.subject + "'?"
        setIdToDelete(email.id)
        setDeleteConfirmContentString(contentString)
        setShowDeleteConfirmationPopup(true)
    }

    const handleDeleteCancel = () => {
        setShowDeleteConfirmationPopup(false)
    }

    const handleDeleteConfirm = async () => {
        const url = '/api/delete-email'
        await axios.post(url, { id: idToDelete })
            .then(() => {
                setShowDeleteConfirmationPopup(false)
                fetchEmails()
                setActivePage(0)
            })
            .catch(err => {
                console.log(`delete email failed with error: ${err}`)
                alert(`Failure deleting email, please try again. Error: ${err}`)
            })
    }

    const renderTableRows = () => {
        if (!filteredEmails.length) return <Table.Row><Table.Cell colSpan="4">No emails found</Table.Cell></Table.Row>
        const startIndex = activePage * itemsPerPage
        const endIndex = startIndex + itemsPerPage
        return filteredEmails.slice(startIndex, endIndex).map(email => (
            <Table.Row key={email.id} onClick={() => handleEmailClick(email)} active={selectedEmail && selectedEmail.id === email.id} style={{ cursor: 'pointer' }}>
                <Table.Cell>{email.to || 'Unknown'}</Table.Cell>
                <Table.Cell>{email.from}</Table.Cell>
                <Table.Cell>{email.subject}</Table.Cell>
                <Table.Cell>{format(new Date(email.date), 'd MMM yyyy, hh:mm a')}</Table.Cell>
                <Table.Cell><Button inverted circular basic icon onClick={e => handleDeleteClicked(e, email)}><Icon inverted name="trash alternate" /></Button></Table.Cell>
            </Table.Row>
        ))
    }

    const renderPagination = () => {
        if (!filteredEmails.length) return null
        const pagesRequired = Math.ceil(filteredEmails.length / itemsPerPage)
        return (
            <Pagination inverted defaultActivePage={1} totalPages={pagesRequired} siblingRange={2} onPageChange={handlePageChange} style={{ marginTop: 20 }}/>
        )
    }

    return (
        <Container style={{ background: '#1b1c1d', color: 'white', minHeight: '100vh'}}>
            <Header style={{ marginTop: 1 }} as="h1" inverted>Emails</Header>
            <Menu inverted pointing secondary>
                <Menu.Item name="Inbox" active={activeTab === 'inbox'} onClick={() => setActiveTab('inbox')} />
                <Menu.Item name="Sent" active={activeTab === 'sent'} onClick={() => setActiveTab('sent')} />
            </Menu>
            <Input inverted icon="search" placeholder="Search by to, from, or subject..." value={searchQuery} onChange={handleSearchChange} style={{ marginBottom: 20, width: '100%', border: '1px #333 solid' }} input={<input style={{ backgroundColor: '#1b1c1d', color: 'white' }}/>}/>
            
            {error && <Message negative>{error}</Message>}
            {loading ? (
                <Loader active inverted>Loading emails...</Loader>
            ) : (
                <Table size={window.innerWidth < 768 ? 'small' : null} celled unstackable inverted selectable sortable style={window.innerWidth < 768 ? { overflowX: 'auto', display: 'block', whiteSpace: 'nowrap', marginTop: 20 } : { marginTop: 20 }}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell width={4} sorted={sortColumn === 'to' ? sortDirection : null} onClick={() => handleSort('to')}>
                                To <Icon name={sortColumn === 'to' ? (sortDirection === 'ascending' ? 'sort up' : 'sort down') : 'sort'} />
                            </Table.HeaderCell>
                            <Table.HeaderCell width={4} sorted={sortColumn === 'from' ? sortDirection : null} onClick={() => handleSort('from')}>
                                From <Icon name={sortColumn === 'from' ? (sortDirection === 'ascending' ? 'sort up' : 'sort down') : 'sort'} />
                            </Table.HeaderCell>
                            <Table.HeaderCell width={6} sorted={sortColumn === 'subject' ? sortDirection : null} onClick={() => handleSort('subject')}>
                                Subject <Icon name={sortColumn === 'subject' ? (sortDirection === 'ascending' ? 'sort up' : 'sort down') : 'sort'} />
                            </Table.HeaderCell>
                            <Table.HeaderCell width={4} sorted={sortColumn === 'date' ? sortDirection : null} onClick={() => handleSort('date')}>
                                Date <Icon name={sortColumn === 'date' ? (sortDirection === 'ascending' ? 'sort up' : 'sort down') : 'sort'} />
                            </Table.HeaderCell>
                            <Table.HeaderCell></Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {renderTableRows()}
                    </Table.Body>
                </Table>
            )}
            {renderPagination()}
            {selectedEmail && (
                <Segment inverted style={{ marginTop: 40, padding: 25, border: '1px solid #ccc' }}>
                    <Header as="h3" style={{ marginBottom: 20 }}>{selectedEmail.subject}</Header>
                    <p><strong>To:</strong> {selectedEmail.to || 'Unknown'}</p>
                    <p><strong>From:</strong> {selectedEmail.from}</p>
                    <p style={{ marginBottom: 25 }}>
                        <strong>On: </strong>
                        {format(new Date(selectedEmail.date), 'd MMM yyyy, hh:mm a')}
                    </p>
                    <div style={{ background: '#2a2b2c', padding: '1em', borderRadius: 4 }} dangerouslySetInnerHTML={{ __html: selectedEmail.body }}/>
                    <Button style={{ marginTop: 25, marginBottom: 35, marginRight: 10 }} inverted onClick={() => setSelectedEmail(null)}>Close</Button>
                    <Button inverted color="teal" onClick={() => setIsReplying(!isReplying)}>{isReplying ? 'Cancel Reply' : 'Reply'}</Button>
                    { isReplying && ( 
                        <Form inverted>
                            <Form.Field>
                                <label>Reply</label>
                                <TextArea placeholder="Type your reply here..." value={replyContent} onChange={(e) => setReplyContent(e.target.value)} rows={5} className="dark-theme-textarea"/>
                            </Form.Field>
                            <Button style={{ marginTop: 15 }} inverted color="teal" onClick={handleSendReply}>Send Reply</Button>
                            {replyError && <Message negative>{replyError}</Message>}
                            {replySuccess && <Message positive>{replySuccess}</Message>}
                        </Form>
                    )}
                </Segment>
            )}
            <Confirm inverted open={showDeleteConfirmationPopup} onCancel={handleDeleteCancel} onConfirm={handleDeleteConfirm} header="Confirmation" content={deleteConfirmContentString} cancelButton="Cancel" confirmButton="Delete"/>
        </Container>
    )
}

export default Emails